import { useMemo, useRef, useEffect, useCallback } from 'react';

import { IconButton, Tooltip } from '@vimeo/bokeh';
import { QuestionCircle } from '@vimeo/bokeh-icons';
import { useCapability } from '@vimeo/core/hooks/use-capability';
import { translate } from '@vimeo/i18n-runtime';
import { useViewer } from '@vimeo/viewer';

import { trackNavigationActionEvent } from '../../tracking';
import { HelpPanel } from './help-panel/components/HelpPanel';
import { HelpPanelProvider } from './help-panel/context/HelpPanelContext';
import { useNewIPSPanelAssignment } from './help-panel/hooks/use-new-ips-assignment';
import { useHelpPanel } from './help-panel/hooks/useHelpPanel';
import { useTrackHelpPanelEvent } from './help-panel/utils/bigPicture';
import { addRecursiveClickEvent } from './utils';

const HelpButtonAndPanel = () => {
  const viewer = useViewer();
  const { openPanel, closePanel, isPanelOpen } = useHelpPanel();
  const trackHelpPanelEvent = useTrackHelpPanelEvent();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const ownerId = useMemo(
    () => viewer?.teamUser?.ownerId ?? viewer?.user?.id,
    [viewer?.teamUser?.ownerId, viewer?.user?.id],
  );

  const { capabilities: ownerCapabilities } = useCapability(['hasEnterprise'], ownerId);
  const hasEnterprise = ownerCapabilities.hasEnterprise;
  const viewerLocale = viewer?.locale;
  const isSimplifiedSite = viewer?.isSimplifiedSite;

  const isEnglishUser = useMemo(() => viewerLocale === 'en', [viewerLocale]);

  const shouldUseNewIpsPanel = useNewIPSPanelAssignment();
  const shouldShowNewHelpPanel = shouldUseNewIpsPanel && isEnglishUser && !isSimplifiedSite;

  const buttonProps = useMemo(() => {
    if (shouldShowNewHelpPanel) {
      // For staff English users with no simplified site, new help panel
      return { 'data-id': 'help_link', as: 'button' as const };
    }
    // others, legacy help
    if (hasEnterprise) {
      return { 'data-id': 'enterprise_help_button', as: 'button' as const };
    }
    return {
      'data-id': 'help_link',
      as: 'a' as const,
      href: '/help/sso?redirect_to=https://help.vimeo.com/',
      target: '_blank',
    };
  }, [shouldShowNewHelpPanel, hasEnterprise]);

  const handleClick = () => {
    if (shouldShowNewHelpPanel) {
      if (isPanelOpen) {
        closePanel();
      } else {
        openPanel(hasEnterprise);
        trackHelpPanelEvent({
          eventName: 'vimeo.in_app_support_panel_launch_button',
          element: 'button',
        });
      }
    } else {
      addRecursiveClickEvent(
        'pendo-link-9e2e9ac7-1',
        'pendo-g-gGIq9Pjwp5KjK6P-yRQzNQ_Zy-g',
        'pendo-left-caret-229848f1',
        'pendo-g-0VqsBgo3lrYNn-v0y0Mj3DYh46k',
      );
      trackNavigationActionEvent({
        copy: 'help',
        element: 'button',
        feature: 'help_center',
        viewer,
      });
    }
  };

  const onDocumentClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }

      closePanel();
    },
    [closePanel],
  );

  useEffect(() => {
    document.addEventListener<any>('click', onDocumentClick, true);

    return () => {
      document.removeEventListener<any>('click', onDocumentClick, true);
    };
  }, [onDocumentClick]);

  return (
    <>
      <Tooltip isDisabled={hasEnterprise || isPanelOpen} label={translate('Help')}>
        <IconButton
          ref={buttonRef}
          aria-label="help_button"
          icon={<QuestionCircle />}
          variant="tertiary"
          onClick={handleClick}
          size={{ base: 'sm', sm: 'md' }}
          isActive={isPanelOpen}
          {...buttonProps}
        />
      </Tooltip>
      {shouldShowNewHelpPanel && <HelpPanel ref={panelRef} />}
    </>
  );
};

export const HelpCTA = () => {
  return (
    <HelpPanelProvider>
      <HelpButtonAndPanel />
    </HelpPanelProvider>
  );
};
